import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Box from "components/common/Box";
import BrandSection from "components/allInOneDashboard/nav/BrandSection";
import NavLinks from "components/allInOneDashboard/nav/NavLinks";
import HeaderActions from "components/allInOneDashboard/nav/HeaderActions";
import AllInOneSideDrawer from "components/allInOneDashboard/nav/AllInOneSideDrawer";
import NotificationDrawer from "components/allInOneDashboard/NotificationDrawer";
import { TOP_NAV_ITEMS } from "components/allInOneDashboard/constants/topNavConfig";
import {
  HEADER_SX,
  NATIVE_HEADER_OFFSET,
  NATIVE_HEADER_SX,
} from "components/allInOneDashboard/constants/navTheme";
import {
  MARKET_PULSE_HUB_PATH,
  UNIFIED_DASHBOARD_PATH,
} from "components/allInOneDashboard/constants/productRouteMap";
import { config } from "config";
import { axios } from "helper/axios";
import { parseNotificationsResponse } from "utils/parseNotifications";
import { useAppLogout } from "hooks/useAppLogout";
import { localStore } from "stores/localStorage";
import LogoutConfirmModal from "components/allInOneDashboard/profile/LogoutConfirmModal";

const AllInOneTopNav = ({ isProfileDisabled = false }) => {
  const history = useHistory();
  const location = useLocation();
  const isNativeApp = config.isMobileApp;
  const handleLogout = useAppLogout();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const showLogout = !localStore.isSsoCustomer();

  useEffect(() => {
    let cancelled = false;

    const loadUnread = async () => {
      try {
        const response = await axios.get("dashboard/notification-data", {
          params: { page: 1, limit: 10 },
          skipErrorToast: true,
        });
        if (cancelled) return;
        const { notifications } = parseNotificationsResponse(response);
        const unread = notifications.filter((item) => !item.isSeen).length;
        setUnreadCount(unread);
      } catch (err) {
        console.error("Failed to load notification unread count:", err);
      }
    };

    loadUnread();
    return () => {
      cancelled = true;
    };
  }, []);

  const isActive = useCallback(
    (item) => {
      if (!item.path) return false;
      if (item.id === "explore") {
        return location.pathname === UNIFIED_DASHBOARD_PATH;
      }
      if (item.id === "marketPulse") {
        return (
          location.pathname === MARKET_PULSE_HUB_PATH ||
          location.pathname.startsWith(`${MARKET_PULSE_HUB_PATH}/`)
        );
      }
      return (
        location.pathname === item.path ||
        location.pathname.startsWith(`${item.path}/`)
      );
    },
    [location.pathname],
  );

  const handleNavClick = (item) => {
    if (item.id === "profile" && isProfileDisabled) {
      return;
    }
    if (item.path) {
      history.push(item.path);
    }
  };

  const isItemDisabled = (item) =>
    item.id === "profile" && isProfileDisabled;

  const handleProfileClick = () => {
    if (isProfileDisabled) return;
    history.push("/customer/profile");
  };

  const openLogoutModal = () => setIsLogoutOpen(true);
  const closeLogoutModal = () => setIsLogoutOpen(false);
  const confirmLogout = () => {
    setIsLogoutOpen(false);
    handleLogout();
  };

  return (
    <>
      <Box component="header" sx={isNativeApp ? NATIVE_HEADER_SX : HEADER_SX}>
        <Box
          sx={{
            width: "100%",
            display: "grid",
            // Brand left | nav links + actions right (web desktop)
            gridTemplateColumns: {
              xs: "1fr auto",
              md: "auto 1fr",
            },
            alignItems: "center",
            gap: { xs: 1, md: 2 },
            minWidth: 0,
          }}
        >
          <BrandSection />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: { xs: 1, md: 2, lg: 3 },
              minWidth: 0,
            }}
          >
            {/* Desktop web only: horizontal top nav links on the right */}
            {!isNativeApp ? (
              <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}>
                <NavLinks
                  items={TOP_NAV_ITEMS}
                  isActive={isActive}
                  onItemClick={handleNavClick}
                  isItemDisabled={isItemDisabled}
                />
              </Box>
            ) : null}

            <HeaderActions
              onNotificationClick={() => setIsNotificationOpen(true)}
              onProfileClick={handleProfileClick}
              onMenuClick={() => setIsSideMenuOpen(true)}
              onLogoutClick={showLogout ? openLogoutModal : undefined}
              isProfileDisabled={isProfileDisabled}
              unreadCount={unreadCount}
            />
          </Box>
        </Box>
      </Box>

      {/* Reserve space under fixed native header so content isn't covered. */}
      {isNativeApp ? (
        <Box aria-hidden sx={{ height: NATIVE_HEADER_OFFSET, flexShrink: 0 }} />
      ) : null}

      {/* Mobile-web side menu */}
      {!isNativeApp ? (
        <AllInOneSideDrawer
          open={isSideMenuOpen}
          onClose={() => setIsSideMenuOpen(false)}
          isActive={isActive}
          onItemClick={handleNavClick}
          isItemDisabled={isItemDisabled}
          onLogoutClick={showLogout ? openLogoutModal : undefined}
        />
      ) : null}

      <NotificationDrawer
        open={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
        onUnreadCountChange={setUnreadCount}
      />

      <LogoutConfirmModal
        open={isLogoutOpen}
        onClose={closeLogoutModal}
        onConfirm={confirmLogout}
      />
    </>
  );
};

export default AllInOneTopNav;
